import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef} from "react";
import {EventEmitter} from "nate-react-api-helpers";
import {useSyncedRef} from "../SyncedRef";

export const UndoContext = createContext({
    push: (undo: UndoCallback) => {
        console.error("invalid undo context")
    },
    onCanUndo: new EventEmitter<boolean>(),
    undo: () => {

    },
    reset: () => {

    }
})

type UndoCallback = () => Promise<any>

export function UndoProvider(props: PropsWithChildren<any>) {
    const onCanUndo = useMemo(() => new EventEmitter<boolean>(), []);
    const undoListRef = useRef<UndoCallback[]>([]);
    const inProgressRef = useRef(false);

    const undo = useCallback(async () => {
        if(inProgressRef.current) return;

        const last = undoListRef.current.pop();
        if(undoListRef.current.length === 0) {
            onCanUndo.emit(false);
        }

        if(!last) {
            return;
        }

        inProgressRef.current = true;

        try {
            await last();
        } catch (e: any) {}

        inProgressRef.current = false;
    }, [onCanUndo]);

    useEffect(() => {
        const undoKeyCommand = (e: KeyboardEvent) => {
            if(e.key === "z" && (e.ctrlKey || e.metaKey)) {
                undo();
            }
        }

        document.body.addEventListener("keydown", undoKeyCommand)

        return () => {
            document.body.removeEventListener("keypress", undoKeyCommand)
        }
    }, [undo]);

    const push = useCallback((input: UndoCallback) => {
        undoListRef.current.push(input)
        if(undoListRef.current.length === 1) {
            onCanUndo.emit(true);
        }
    }, [onCanUndo, undoListRef])

    const ctx = useMemo(() => ({
        push: push,
        onCanUndo: onCanUndo,
        undo: undo,
        reset: () => {
            undoListRef.current = [];
            onCanUndo.emit(false);
        }
    }), [onCanUndo, undo, push])

    return (
        <UndoContext.Provider value={ctx}>
            {props.children}
        </UndoContext.Provider>
    )
}

export function useUndoPush() {
    const ctx = useContext(UndoContext);
    const pushRef = useSyncedRef(ctx.push);
    const resetRef = useSyncedRef(ctx.reset);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => resetRef.current();
    }, [resetRef]);

    return useCallback((input: UndoCallback) => {
        pushRef.current(input);
    }, [pushRef]);
}

export function useTypingUndo(props: {
    current: string,
    onRestore: (input: string) => Promise<void>,
    ignore: boolean;
}) {
    const lastValueRef = useRef(props.current);
    const lastTmRef = useRef(Date.now());
    const pushUndo = useUndoPush();
    const current = props.current;
    const currentRef = useSyncedRef(props.current);
    const ignore = props.ignore;
    const lastIgnoreRef = useRef(props.ignore);

    const onRestoreRef = useSyncedRef(props.onRestore);

    useEffect(() => {
        // when ignore is lifted, reset last* values
        if(lastIgnoreRef.current) {
            lastValueRef.current = currentRef.current;
            lastTmRef.current = Date.now();
        }

        lastIgnoreRef.current = props.ignore
    }, [props.ignore, currentRef]);

    useEffect(() => {
        if(props.ignore) return;
        if(lastValueRef.current === current) return;

        const delayMs = 800;
        const since = Date.now() - lastTmRef.current;

        const tm = setTimeout(() => {
            if(lastValueRef.current === current) return;

            const old = lastValueRef.current;
            lastValueRef.current = current;
            lastTmRef.current = Date.now();
            pushUndo(() => onRestoreRef.current(old));
        }, Math.max(delayMs - since, 0));

        return () => clearTimeout(tm);
    }, [current, ignore, onRestoreRef, pushUndo, props.ignore]);
}